import React, { useContext } from 'react';
import ThemeContext from '../ThemeContext';

const Pagination = ({
  currentPage,
  setCurrentPage,
  perPage,
  setPerPage,
  totalItems,
  gotoPage,
  setGotoPage,
}) => {
  const { theme } = useContext(ThemeContext);
  const totalPages = Math.ceil(totalItems / perPage);

  const handlePerPageChange = (e) => {
    setPerPage(Number(e.target.value));
    setCurrentPage(1);
  };

  const handleGoToPage = () => {
    const page = parseInt(gotoPage, 10);
    if (!isNaN(page) && page >= 1 && page <= totalPages) {
      setCurrentPage(page);
    }
    setGotoPage('');
  };

  const getPageNumbers = () => {
    const pages = [];
    const startPage = Math.max(1, currentPage - 1);
    const endPage = Math.min(totalPages, startPage + 2);
    for (let i = startPage; i <= endPage; i++) {
      pages.push(i);
    }
    return pages;
  };

  return (
    <div
      style={{
        display: 'flex',
        flexDirection: window.innerWidth < 600 ? 'column' : 'row',
        alignItems: window.innerWidth < 600 ? 'stretch' : 'center',
        justifyContent: 'space-between',
        width: '100%',
        borderRadius: '6px',
        background: theme.gradients.secondary,
        color: theme.colors.textWhite,
        fontSize: 'clamp(10px, 2.5vw, 11px)',
        gap: 'clamp(0.3rem, 2vw, 0.5rem)',
        padding: 'clamp(3px, 1vw, 5px)',
        marginBottom: 'clamp(0.5rem, 2vw, 1rem)',
      }}
    >
      <div style={{ display: 'flex', alignItems: 'center' }}>
        <label
          style={{
            marginRight: '0.5rem',
            marginBottom: 0,
            fontSize: 'clamp(10px, 2.5vw, 11px)',
            color: theme.colors.textWhite,
          }}
        >
          Display
        </label>
        <select
          value={perPage}
          onChange={handlePerPageChange}
          className="form-select form-select-sm"
          style={{
            width: '60px',
            backgroundColor: theme.colors.bgPrimary,
            color: theme.colors.textPrimary,
            borderRadius: '5px',
            padding: 'clamp(3px, 1vw, 4px)',
            fontSize: 'clamp(10px, 2.5vw, 11px)',
            outline: 'none',
            boxShadow: 'none',
            border: 'none',
          }}
        >
          <option value={10}>10</option>
          <option value={20}>20</option>
          <option value={30}>30</option>
        </select>
      </div>
      <div style={{ fontSize: 'clamp(10px, 2.5vw, 11px)' }}>
        Showing {Math.min(currentPage * perPage, totalItems)} of {totalItems}
      </div>
      <div style={{ display: 'flex', alignItems: 'center' }}>
        <label
          style={{
            marginRight: '0.5rem',
            marginBottom: 0,
            fontSize: 'clamp(10px, 2.5vw, 11px)',
            color: theme.colors.textWhite,
          }}
        >
          Go to
        </label>
        <input
          type="number"
          value={gotoPage}
          onChange={(e) => setGotoPage(e.target.value)}
          className="form-control form-control-sm"
          style={{
            width: '55px',
            backgroundColor: theme.colors.bgPrimary,
            color: theme.colors.textPrimary,
            borderRadius: '5px',
            padding: 'clamp(3px, 1vw, 4px)',
            fontSize: 'clamp(10px, 2.5vw, 11px)',
            outline: 'none',
            boxShadow: 'none',
            border: 'none',
          }}
        />
        <button
          className="btn btn-sm"
          onClick={handleGoToPage}
          style={{
            background: theme.gradients.primary,
            color: theme.colors.textWhite,
            border: 'none',
            borderRadius: '5px',
            padding: 'clamp(3px, 1vw, 4px) clamp(8px, 2vw, 10px)',
            fontSize: 'clamp(10px, 2.5vw, 11px)',
            transition: 'opacity 0.3s ease',
            boxShadow: 'none',
            marginLeft: '0.5rem',
          }}
          onMouseOver={(e) => (e.target.style.opacity = '0.9')}
          onMouseOut={(e) => (e.target.style.opacity = '1')}
        >
          Go
        </button>
      </div>
      <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
        <button
          onClick={() => setCurrentPage((prev) => Math.max(prev - 1, 1))}
          disabled={currentPage === 1}
          style={{
            background: 'none',
            border: 'none',
            color: theme.colors.textWhite,
            fontSize: 'clamp(10px, 2.5vw, 11px)',
            cursor: currentPage === 1 ? 'not-allowed' : 'pointer',
            opacity: currentPage === 1 ? 0.5 : 1,
            transition: 'opacity 0.3s ease',
          }}
          onMouseOver={(e) => currentPage !== 1 && (e.target.style.opacity = '0.8')}
          onMouseOut={(e) => currentPage !== 1 && (e.target.style.opacity = '1')}
        >
          Prev
        </button>
        {getPageNumbers().map((page) => (
          <button
            key={page}
            onClick={() => setCurrentPage(page)}
            style={{
              background: currentPage === page ? theme.colors.bgPrimary : 'none',
              border: 'none',
              borderRadius: '50%',
              width: '24px',
              height: '24px',
              color: theme.colors.textSecondary || '#333',
              fontSize: 'clamp(10px, 2.5vw, 11px)',
              cursor: 'pointer',
              transition: 'background 0.3s ease, opacity 0.3s ease',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
            onMouseOver={(e) => (e.target.style.opacity = '0.8')}
            onMouseOut={(e) => (e.target.style.opacity = '1')}
          >
            {page}
          </button>
        ))}
        <button
          onClick={() => setCurrentPage((prev) => Math.min(prev + 1, totalPages))}
          disabled={currentPage === totalPages}
          style={{
            background: 'none',
            border: 'none',
            color: theme.colors.textWhite,
            fontSize: 'clamp(10px, 2.5vw, 11px)',
            cursor: currentPage === totalPages ? 'not-allowed' : 'pointer',
            opacity: currentPage === totalPages ? 0.5 : 1,
            transition: 'opacity 0.3s ease',
          }}
          onMouseOver={(e) => currentPage !== totalPages && (e.target.style.opacity = '0.8')}
          onMouseOut={(e) => currentPage !== totalPages && (e.target.style.opacity = '1')}
        >
          Next
        </button>
      </div>
    </div>
  );
};

export default Pagination;